import { extractErrorDetails, type ClientErrorSource } from "./format";

/**
 * Browser-side error reporter. POSTs to /api/client-error, which logs the
 * error in GCP Error Reporting format. Deduplicates within the page lifetime
 * and hard-caps report volume so an error loop can't spam the endpoint.
 */
const MAX_REPORTS_PER_PAGE = 10;

const reported = new Set<string>();
let reportCount = 0;

export function reportClientError(
  error: unknown,
  options: { source: ClientErrorSource; digest?: string },
): void {
  try {
    if (typeof window === "undefined") return;

    const { message, stack, digest } = extractErrorDetails(error);
    const key = `${message}|${(stack ?? "").slice(0, 200)}`;
    if (reported.has(key) || reportCount >= MAX_REPORTS_PER_PAGE) return;
    reported.add(key);
    reportCount += 1;

    // keepalive lets the report survive navigation away from a broken page.
    void fetch("/api/client-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        message,
        stack,
        source: options.source,
        digest: options.digest ?? digest,
        url: window.location.href,
        release: process.env.NEXT_PUBLIC_RELEASE,
      }),
    }).catch(() => {});
  } catch {
    // Reporting must never break the page it is observing.
  }
}
